"use client";

import { useCallback, useState, useRef, useEffect } from "react";
import { Autocomplete, Input, Popover, TextField } from "react-aria-components";
import { Icon } from "@zapier/zinnia-icons";
import { BetaShortcut } from "@zapier/design-system";
import { GlobalSearchEvent, type GlobalSearchEmitFunction } from "../types";
import { GlobalSearchDropdownView } from "./GlobalSearchDropdown";
import { getSearchHistory, clearSearchHistory } from "../hooks/searchHistory";
import { useSearchDropdownData } from "../hooks/useSearchDropdownData";
import styles from "./GlobalSearchInput.module.css";

export type { GlobalSearchEmitPayload } from "../types";
export { GlobalSearchEvent } from "../types";

export interface GlobalSearchInputProps {
  /** Emit function for analytics. The package calls this at every trackable interaction. */
  emit?: GlobalSearchEmitFunction;
  /** Override the default placeholder text. */
  placeholder?: string;
  /** Injected the same way `emit` is: this package cannot read host flags. */
  includeNextGenZaps?: boolean;
}

export function GlobalSearchInput({
  emit,
  placeholder = "Search assets, apps, templates, and more",
  includeNextGenZaps,
}: GlobalSearchInputProps) {
  const [isClient, setIsClient] = useState(false);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  // Global search sits in the app chrome on every page, so the dropdown's
  // requests wait for the first open rather than firing on every page load.
  const [hasOpened, setHasOpened] = useState(false);
  const inputReference = useRef<HTMLInputElement>(null);
  const triggerReference = useRef<HTMLDivElement>(null);
  const popoverReference = useRef<HTMLDivElement>(null);

  const openSearch = useCallback(() => {
    setIsOpen(true);
    setHasOpened(true);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) openSearch();
      else setIsOpen(false);
    },
    [openSearch],
  );

  // Owned above the popover, which unmounts its subtree on close: a hook called
  // from inside it would refetch and re-emit analytics on every reopen.
  const searchData = useSearchDropdownData({
    query,
    emit,
    includeNextGenZaps,
    initiallyEnabled: hasOpened,
  });

  // History needs to live above the RAC collection in order to keep the history in sync with it.
  // If the history is kept inside the dropdown, old history items will persist after the user presses the "Clear" button.
  // React Aria caches items in the menu collection. Mutating history from local state does not rebuild the collection. Lifting the state to this parent forces the collection to rebuild.
  const [history, setHistory] = useState<string[]>(() => getSearchHistory());

  useEffect(() => {
    if (query.length === 0) setHistory(getSearchHistory());
  }, [query]);

  const clearHistory = () => {
    clearSearchHistory();
    setHistory([]);
  };

  // Selecting a recent search / prompt refills the query and keeps the dropdown
  // open so results for the new query appear without another interaction.
  const refillQuery = (value: string) => {
    setQuery(value);
    openSearch();
    inputReference.current?.focus();
  };

  useEffect(() => {
    // `Shortcut` renders platform-specific key glyphs (⌘ vs Ctrl). On the server we don't reliably
    // know the user's platform, so render it client-only to avoid SSR hydration mismatches.
    setIsClient(true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        inputReference.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // `usePopover` sets `isDismissable: !isNonModal`, so a non-modal popover gets
  // no outside-dismiss from React Aria and would stay open over the page.
  useEffect(() => {
    if (!isOpen) return;

    const closeIfOutside = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        triggerReference.current?.contains(target) ||
        popoverReference.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("touchstart", closeIfOutside);
    document.addEventListener("focusin", closeIfOutside);
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("touchstart", closeIfOutside);
      document.removeEventListener("focusin", closeIfOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.root} ref={triggerReference}>
      <Autocomplete
        inputValue={query}
        onInputChange={setQuery}
        // Always show every result: our data layer already returns query-filtered
        // results, so React Aria's client-side text filtering must be disabled.
        filter={() => true}
        // Leave focus in the input after each keystroke. Auto-focusing the first
        // item would make Enter navigate to whichever result happened to load.
        disableAutoFocusFirst
      >
        <TextField aria-label={placeholder} className={styles.field}>
          <div className={styles.inputWrapper}>
            <span className={styles.searchIcon}>
              <Icon name="navSearch" size={20} />
            </span>
            <Input
              ref={inputReference}
              className={styles.input}
              placeholder={placeholder}
              autoComplete="off"
              onFocus={() => {
                openSearch();
                emit?.({
                  event: GlobalSearchEvent.Focus,
                  eventAction: "view",
                });
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") setIsOpen(false);
              }}
            />
            {query ? (
              <button
                type="button"
                className={styles.clearButton}
                aria-label="Clear search"
                onMouseDown={(event_) => event_.preventDefault()}
                onClick={() => {
                  setQuery("");
                  inputReference.current?.focus();
                }}
              >
                <Icon name="formX" size={20} />
              </button>
            ) : (
              isClient && (
                <div className={styles.shortcutOverlay}>
                  <BetaShortcut keys={["cmd", "k"]} variant="light" />
                </div>
              )
            )}
          </div>
        </TextField>
        <Popover
          ref={popoverReference}
          className={styles.popover}
          offset={4}
          triggerRef={triggerReference}
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          // Keep focus in the input while arrow keys move virtual focus through
          // the menu; a modal popover would trap focus inside the dropdown.
          isNonModal
        >
          <div className={styles.dropdownBody}>
            <GlobalSearchDropdownView
              query={query}
              onPromptClick={refillQuery}
              emit={emit}
              history={history}
              onClearHistory={clearHistory}
              data={searchData}
            />
          </div>
          <div className={styles.dropdownFooter}>
            <span className={styles.footerHint}>
              <kbd className={styles.kbd}>↑</kbd>
              <kbd className={styles.kbd}>↓</kbd>
              <span className={styles.footerLabel}>Navigate</span>
            </span>
            <span className={styles.footerHint}>
              <BetaShortcut keys="enter" variant="light" />
              <span className={styles.footerLabel}>Select</span>
            </span>
            <span className={styles.footerHint}>
              <BetaShortcut keys="escape" variant="light" />
              <span className={styles.footerLabel}>Close</span>
            </span>
          </div>
        </Popover>
      </Autocomplete>
    </div>
  );
}
